import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Pause, Play, ChevronDown, ChevronUp } from "lucide-react";
import { motion } from 'framer-motion';
import * as musicMetadata from 'music-metadata-browser';
import { useAbortController } from '../../../hooks/useAbortController.js';
import { useSelector } from 'react-redux';
import { selectIsVoiceFlow } from "../../../store/features/message/textContentSelectors.js"
import { selectIsMicButtonClicked } from '../../../store/features/ui/uiSelectors.js';
import PropTypes from "prop-types";


const AudioPlayer = ({ contentAudio, isAutoPlay = false, showText, setShowText }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
    const isVoiceFlow = useSelector(selectIsVoiceFlow);
    const isMicButtonClicked = useSelector(selectIsMicButtonClicked);

    const audioRef = useRef(null);
    const animationRef = useRef(null);
    const isDragging = useRef(false);
    const lastUpdateTime = useRef(0);
    const seekingTimeoutRef = useRef(null);
    const isFirstRender = useRef(true);
    const processedAudioRef = useRef(null);

    const {
        getAudioController,
        updateAudioState,
        // abortAudio
    } = useAbortController();

    const abortController = getAudioController();

    const codecToMimeType = useCallback((codec, container) => {
        const codecLower = codec?.toLowerCase() || '';
        const containerLower = container?.toLowerCase() || '';

        if (codecLower.includes('mp3') || codecLower.includes('mpeg') || containerLower === 'mpeg') {
            return 'audio/mpeg';
        }
        if (codecLower.includes('opus') || containerLower === 'opus') {
            return 'audio/opus';
        }
        if (codecLower.includes('vorbis') || containerLower === 'ogg') {
            return 'audio/ogg';
        }
        if (codecLower.includes('aac') || containerLower === 'mp4' || containerLower === 'm4a') {
            return 'audio/mp4';
        }
        if (codecLower.includes('flac') || containerLower === 'flac') {
            return 'audio/flac';
        }
        if (containerLower === 'wav' || containerLower === 'wave') {
            return 'audio/wav';
        }
        return 'audio/wav';
    }, []);

    const processAudioData = useCallback(async (base64Data) => {
        try {
            if (isFirstRender.current) {
                updateAudioState({ isLoading: true });
                isFirstRender.current = false;
            }

            if (abortController.signal.aborted) {
                throw new Error('Audio processing aborted');
            }

            const byteString = atob(base64Data);
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const uint8Array = new Uint8Array(arrayBuffer);
            for (let i = 0; i < byteString.length; i++) {
                if (i % 10000 === 0 && abortController.signal.aborted) {
                    throw new Error('Audio processing aborted');
                }
                uint8Array[i] = byteString.charCodeAt(i);
            }

            let detectedMimeType = 'audio/mpeg';
            let metadata = null;

            try {
                const tempBlob = new Blob([uint8Array]);
                metadata = await musicMetadata.parseBlob(tempBlob);

                if (abortController.signal.aborted) {
                    throw new Error('Audio processing aborted');
                }

                if (metadata.format) {
                    detectedMimeType = codecToMimeType(metadata.format.codec, metadata.format.container);

                    if (metadata.format.duration && isFinite(metadata.format.duration)) {
                        setDuration(metadata.format.duration);
                    }
                }
            } catch (metadataError) {
                console.warn('Could not parse audio metadata:', metadataError);
            }
            // setMimeType(detectedMimeType);
            const blob = new Blob([uint8Array], { type: detectedMimeType });
            const url = URL.createObjectURL(blob);
            setAudioUrl(url);
            updateAudioState({ isLoading: false });
        } catch (error) {
            console.error('Error processing audio:', error);
            updateAudioState({ isLoading: false });
        }
    }, [codecToMimeType, abortController.signal, updateAudioState]);

    useEffect(() => {
        const handleAbort = () => {

            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }

            setIsPlaying(false);
            setCurrentTime(0);

            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
                setAudioUrl(null);
            }

            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }

            if (seekingTimeoutRef.current) {
                clearTimeout(seekingTimeoutRef.current);
            }
        };

        abortController.signal.addEventListener('abort', handleAbort);

        return () => {
            abortController.signal.removeEventListener('abort', handleAbort);
        };
    }, [abortController.signal, audioUrl]);

    useEffect(() => {
        if (!contentAudio || abortController.signal.aborted) {
            setAudioUrl(null);
            setDuration(0);
            setCurrentTime(0);
            processedAudioRef.current = null;
            return;
        }

        if (processedAudioRef.current === contentAudio) {
            return;
        }

        updateAudioState({ isLoading: true });

        const processAudio = async () => {
            try {
                const base64Data = contentAudio.includes(',')
                    ? contentAudio.split(',')[1]
                    : contentAudio;
                await processAudioData(base64Data);
                processedAudioRef.current = contentAudio;
            } catch (error) {
                if (!error.message.includes('aborted')) {
                    console.error('Error in audio processing:', error);
                }
                updateAudioState({ isLoading: false });
            }
        };

        processAudio();

        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            if (seekingTimeoutRef.current) clearTimeout(seekingTimeoutRef.current);
        };
    }, [contentAudio, abortController.signal]);

    const updateProgress = useCallback(() => {
        if (audioRef.current && !isDragging.current && !abortController.signal.aborted) {
            const now = Date.now();
            if (now - lastUpdateTime.current >= 16) {
                const time = audioRef.current.currentTime;
                if (Math.abs(time - currentTime) > 0.01) {
                    setCurrentTime(time);
                }
                lastUpdateTime.current = now;
            }

            if (isPlaying) {
                animationRef.current = requestAnimationFrame(updateProgress);
            }
        }
    }, [isPlaying, currentTime, abortController.signal]);

    useEffect(() => {
        if (isPlaying && !abortController.signal.aborted) {
            animationRef.current = requestAnimationFrame(updateProgress);
        } else if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [isPlaying, updateProgress, abortController.signal]);

    useEffect(() => {
        if (isAutoPlay && audioUrl && audioRef.current && !hasAutoPlayed && !abortController.signal.aborted) {
            const playAudio = async () => {
                try {
                    await audioRef.current.play();
                    setIsPlaying(true);
                    updateAudioState({ isPlaying: true });
                    setHasAutoPlayed(true);
                } catch (err) {
                    console.error('Auto-play failed:', err);
                }
            };
            const timeout = setTimeout(playAudio, 100);
            return () => clearTimeout(timeout);
        }
    }, [audioUrl, isAutoPlay, hasAutoPlayed, abortController.signal, updateAudioState, isMicButtonClicked]);

    // ✅ Pause audio when mic button is clicked
    useEffect(() => {
        if (isMicButtonClicked && audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
            updateAudioState({ isPlaying: false });
        }
    }, [isMicButtonClicked]);


    const handlePlayPause = async () => {
        if (!audioRef.current || abortController.signal.aborted) return;
        try {
            if (isPlaying) {
                audioRef.current.pause();
                setIsPlaying(false);
                updateAudioState({ isPlaying: false });
            } else {
                await audioRef.current.play();
                setIsPlaying(true);
                updateAudioState({ isPlaying: true });
            }
        } catch (err) {
            console.error('Play/pause failed:', err);
        }
    };

    const handleLoadedMetadata = (e) => {
        if (abortController.signal.aborted) return;

        const audioDuration = e.target.duration;
        if ((!duration || duration === 0) && isFinite(audioDuration)) {
            setDuration(audioDuration);
        }
    };

    const handleEnded = () => {
        if (abortController.signal.aborted) return;

        setIsPlaying(false);
        updateAudioState({ isPlaying: false });
        setCurrentTime(0);
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
        }
    };

    const handleTimeUpdate = () => {
        if (!isDragging.current && audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const formatTime = (time) => {
        if (isNaN(time) || !isFinite(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };


    if (abortController.signal.aborted) {
        return null;
    }

    const waveformBarKeys = useMemo(
        () => Array.from({ length: 105 }, () => crypto.randomUUID()),
        []
    );


    return audioUrl ? (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="flex flex-col gap-2">
            <div className="flex items-center gap-3 px-2 py-2 rounded-md
        shadow-sm border border-white" style={{ background: "linear-gradient(to bottom, #0E369E, #007E86)" }}>
                <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handlePlayPause}
                    className="flex-shrink-0 flex text-[#fff] p-2 rounded-full border border-white bg-[#3858C8] items-center justify-center  transition-colors"
                    disabled={!audioUrl}
                >
                    {isPlaying ? (
                        <Pause size={17} fill="#fff" fontSize="large" />
                    ) : (
                        <Play size={17} fill="#fff" fontSize="large" />
                    )}

                </motion.button>

                <div className="flex items-center gap-2 flex-1 min-w-[100px]">
                    <span className="text-xs text-white font-mono">{formatTime(currentTime)}</span>

                    <div className="relative flex flex-1 items-center gap-[2px] overflow-hidden" >
                        {Array.from({ length: 105 }).map((_, i) => {
                            const progress = duration > 0 ? currentTime / duration : 0;
                            const filled = i / 100 < progress;
                            const height = 4 + ((i * 37) % 15);
                            return (
                                <div
                                    key={waveformBarKeys[i]}
                                    className={`w-[2px] min-w-[2px] rounded-sm transition-colors duration-200 ${filled ? "bg-white" : "bg-white/40"
                                        }`}
                                    style={{ height: `${height}px` }}
                                />
                            );
                        })}
                        <input
                            type="range"
                            min="0"
                            max={duration}
                            step="0.01"
                            value={currentTime}
                            onChange={(e) => (audioRef.current.currentTime = e.target.value)}
                            className="
      absolute inset-0 w-full h-full
      opacity-0 cursor-pointer
    "
                        />

                        <div
                            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#3858C8] border-2 border-white shadow-md pointer-events-none"
                            style={{
                                left: `${(currentTime / duration) * 100}%`,
                                transform: "translate(-0%, -50%)",
                            }}
                        />
                    </div>
                    <span className="text-xs text-white font-mono ">{formatTime(duration)}</span>
                </div>

                <audio
                    ref={audioRef}
                    src={audioUrl}
                    onLoadedMetadata={handleLoadedMetadata}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleEnded}
                    onSeeked={() => {
                        if (audioRef.current) {
                            setCurrentTime(audioRef.current.currentTime);
                        }
                    }}
                    preload="metadata"
                />
                <button
                    className={`flex justify-center items-center w-6 h-6 rounded-full border border-white bg-[#3858C8] cursor-pointer text-white ${isVoiceFlow ? "" : "hidden"}`}
                    onClick={() => setShowText(!showText)}
                >
                    {showText ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
            </div>
        </motion.div>
    ) : null;
};

AudioPlayer.propTypes = {
    contentAudio: PropTypes.string.isRequired,
    isAutoPlay: PropTypes.bool,
    showText: PropTypes.bool.isRequired,
    setShowText: PropTypes.func.isRequired,
};

export default AudioPlayer;